import type { ReactNode, Ref } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DocumentViewportLeftRail,
  DocumentViewportRailGroup,
} from "@/features/document-viewport/toolbar";
import { cn } from "@/lib/utils";

const RAIL_BUTTON_BASE =
  "h-[60px] w-[60px] rounded-none border-x border-t border-b-0 border-border/70 bg-background/95 px-1 py-1.5 text-[9px] font-semibold uppercase tracking-[0.1em] shadow-none backdrop-blur flex-col gap-1 last:border-b";

function getRailLabelClassName(label: string) {
  if (label.length >= 11) {
    return "text-[6px] tracking-[0.04em]";
  }

  if (label.length >= 9) {
    return "text-[7px] tracking-[0.06em]";
  }

  return "text-[8px] tracking-[0.08em]";
}

export interface ViewportToolsetHostItem {
  id: string;
  icon: ReactNode;
  label: string;
  active?: boolean;
  selected?: boolean;
  disabled?: boolean;
  onClick: () => void;
  showArrow?: boolean;
  sublabel?: string;
  showExpandedIndicator?: boolean;
  flyout?: ReactNode;
  showFlyout?: boolean;
}

export function ViewportToolsetButton({
  icon,
  label,
  active,
  selected,
  disabled,
  onClick,
  showArrow,
  sublabel,
  showExpandedIndicator,
}: {
  icon: ReactNode;
  label: string;
  active?: boolean;
  selected?: boolean;
  disabled?: boolean;
  onClick: () => void;
  showArrow?: boolean;
  sublabel?: string;
  showExpandedIndicator?: boolean;
}) {
  const labelClassName = getRailLabelClassName(label);

  return (
    <Button
      type="button"
      variant="ghost"
      disabled={disabled}
      aria-label={sublabel ? `${label}, ${sublabel}` : label}
      onClick={onClick}
      className={cn(
        RAIL_BUTTON_BASE,
        "relative gap-0.5",
        active
          ? "bg-foreground text-background hover:bg-foreground/90 hover:text-background"
          : "hover:bg-muted hover:text-foreground",
        selected && !active && "bg-muted text-foreground"
      )}
    >
      <span className="flex items-center justify-center">{icon}</span>
      {sublabel ? <span aria-hidden="true" className="text-[7px] opacity-70">{sublabel}</span> : null}
      {showArrow ? (
        <ChevronRight className="absolute right-1 top-1/2 size-3 -translate-y-1/2 opacity-50" />
      ) : null}
      <span className={cn("w-full text-center leading-tight break-words", labelClassName)}>
        {label}
      </span>
      {showExpandedIndicator ? (
        <ChevronDown className="absolute bottom-1 left-1/2 size-2.5 -translate-x-1/2" />
      ) : null}
    </Button>
  );
}

export function ViewportToolsetSubButton({
  icon,
  label,
  sublabel,
  sublabelAriaLabel,
  buttonClassName,
  active,
  disabled,
  buttonRef,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  sublabel?: ReactNode;
  sublabelAriaLabel?: string;
  buttonClassName?: string;
  active?: boolean;
  disabled?: boolean;
  buttonRef?: Ref<HTMLButtonElement>;
  onClick: () => void;
}) {
  const labelClassName = getRailLabelClassName(label);
  const resolvedSublabelAriaLabel =
    typeof sublabel === "string" ? sublabel : sublabelAriaLabel;

  return (
    <Button
      ref={buttonRef}
      type="button"
      variant="ghost"
      disabled={disabled}
      aria-label={resolvedSublabelAriaLabel ? `${label}, ${resolvedSublabelAriaLabel}` : label}
      onClick={onClick}
      className={cn(
        RAIL_BUTTON_BASE,
        active
          ? "bg-foreground text-background hover:bg-foreground/90 hover:text-background"
          : "hover:bg-muted hover:text-foreground",
        buttonClassName
      )}
    >
      <span className="flex items-center justify-center">{icon}</span>
      {sublabel ? (
        <span
          aria-hidden="true"
          className="flex w-full items-center justify-center gap-0.5 text-[6px] leading-none opacity-70"
        >
          {sublabel}
        </span>
      ) : null}
      <span className={cn("w-full text-center leading-tight break-words", labelClassName)}>
        {label}
      </span>
    </Button>
  );
}

export function ViewportToolsetHost({
  items,
  inlineContent,
}: {
  items: ViewportToolsetHostItem[];
  inlineContent?: ReactNode;
}) {
  return (
    <DocumentViewportLeftRail>
      <DocumentViewportRailGroup>
        {items.map((item) => (
          <ViewportToolsetButton
            key={item.id}
            icon={item.icon}
            label={item.label}
            active={item.active}
            selected={item.selected}
            disabled={item.disabled}
            onClick={item.onClick}
            showArrow={item.showArrow}
            sublabel={item.sublabel}
            showExpandedIndicator={item.showExpandedIndicator}
          />
        ))}

        {items.map((item, index) =>
          item.flyout && item.showFlyout ? (
            <div
              key={`${item.id}-flyout`}
              className="absolute left-[60px] flex flex-row-reverse"
              style={{ top: `${index * 60}px` }}
            >
              {item.flyout}
            </div>
          ) : null
        )}

        {inlineContent}
      </DocumentViewportRailGroup>
    </DocumentViewportLeftRail>
  );
}
